import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { Avatar, Box, ButtonBase, Card, CardContent, Grid, Tooltip, Typography } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined'
import useTitles from 'common/hooks/useTitles'
import useActions from 'common/hooks/useActions'
import { SAVE } from 'common/components/action/ActionConstant'
import useFetch from 'common/hooks/useFetch'
import ApplicationConf from 'conf/ApplicationConf'
import { isNil } from 'lodash'
import { hasPermission } from 'common/utils/HabilitationUtil'
import { useParams } from 'react-router'
import { updateUserHabs } from 'admin/users/UsersSlice'
import { STATUS } from 'common/constants/storeConstants'
import { CardTitle } from 'common/components/card/Card'
import { getHabAccess, HABILITATIONS } from 'admin/user/UserHabilitations'
import { darkBlue, mainBlue, mediumGrey, secondaryBlue } from 'app/theme'

const AVATAR_SIZE = 120

const CARD_SX = { height: '100%', display: 'flex', flexDirection: 'column' }

const CARD_CONTENT_SX = { p: 3, flex: 1, display: 'flex', alignItems: 'center', '&:last-child': { pb: 3 } }

const InfoLine = ({ icon, label, value }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ display: 'flex', color: mainBlue }}>{icon}</Box>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography sx={{ color: mediumGrey, fontSize: 12, lineHeight: 1.2 }}>{label}</Typography>
            <Typography sx={{ color: darkBlue, fontWeight: 'bold', fontSize: 15 }}>{value}</Typography>
        </Box>
    </Box>
)

InfoLine.propTypes = {
    icon: PropTypes.node,
    label: PropTypes.string,
    value: PropTypes.string,
}

const RoleAccessTooltip = ({ habKey }) => (
    <Box>
        {getHabAccess(habKey).map(({ category, items }) => (
            <Box key={category} sx={{ '&:not(:first-of-type)': { mt: 1 } }}>
                <Typography sx={{ color: secondaryBlue, fontWeight: 'bold', fontSize: '0.8rem', textTransform: 'uppercase', mb: 0.25 }}>
                    {category}
                </Typography>
                {items.map(({ resource, label: menuLabel, access }) => (
                    <Box key={resource} sx={{ display: 'flex', justifyContent: 'space-between', gap: 3, pl: 1 }}>
                        <span>{menuLabel}</span>
                        <span style={{ opacity: 0.7 }}>{access}</span>
                    </Box>
                ))}
            </Box>
        ))}
    </Box>
)

RoleAccessTooltip.propTypes = {
    habKey: PropTypes.string,
}

const RoleTile = ({ habKey, Icon, label, checked, onToggle }) => {
    return (
        <Tooltip title={<RoleAccessTooltip habKey={habKey} />} placement='bottom'>
            <ButtonBase
                role='checkbox'
                aria-checked={checked}
                onClick={onToggle}
                sx={{
                    position: 'relative',
                    width: '100%',
                    height: 110,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    gap: 1,
                    borderRadius: '10px',
                    border: `2px solid ${checked ? mainBlue : 'rgba(22, 24, 50, 0.15)'}`,
                    backgroundColor: checked ? 'rgba(53, 96, 159, 0.08)' : 'transparent',
                    transition: 'background-color 0.2s ease, border-color 0.2s ease',
                    '&:hover': { borderColor: secondaryBlue },
                }}
            >
                {checked && (
                    <CheckCircleIcon sx={{ position: 'absolute', top: 6, right: 6, fontSize: 20, color: mainBlue }} />
                )}
                <Icon sx={{ fontSize: 36, color: checked ? mainBlue : mediumGrey }} />
                <Typography sx={{ color: checked ? darkBlue : mediumGrey, fontWeight: 'bold', fontSize: 14 }}>
                    {label}
                </Typography>
            </ButtonBase>
        </Tooltip>
    )
}

RoleTile.propTypes = {
    habKey: PropTypes.string,
    Icon: PropTypes.elementType,
    label: PropTypes.string,
    checked: PropTypes.bool,
    onToggle: PropTypes.func,
}

const UserApp = () => {
    const dispatch = useDispatch()
    const { propsUser } = useSelector(store => ({ propsUser: store.app.user }), shallowEqual)

    const { login } = useParams()

    const [user, setUser] = useState()

    const {
        data: fetchedUser,
        status,
    } = useFetch(ApplicationConf.auth.user(login))

    useEffect(() => {
        if (status !== STATUS.SUCCEEDED) return
        setUser(fetchedUser)
    }, [fetchedUser, status])

    useTitles(() => [{
        label: 'Utilisateurs',
        href: '/user',
    }, {
        label: login,
        href: `/user/${login}`,
    }], [])

    useActions(() => [{
        type: SAVE,
        onClick: () => dispatch(updateUserHabs({ login, habs: user.habs })),
        displayed: hasPermission(propsUser, 'users', 'update'),
    }], [user])

    const changeHab = hab => {
        if (user.habs.includes(hab)) {
            setUser({ ...user, habs: user.habs.filter(h => h !== hab) })
        } else {
            setUser({ ...user, habs: [...user.habs, hab] })
        }
    }

    return !isNil(user) && (
        <Grid container spacing={1} p={1}>
            <Grid size={{ xs: 12, md: 5 }}>
                <Card sx={CARD_SX}>
                    <CardTitle title='Contact' color={darkBlue} />
                    <CardContent sx={CARD_CONTENT_SX}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                            <Avatar
                                src={user.picture}
                                alt={user.login}
                                variant='rounded'
                                sx={{
                                    width: AVATAR_SIZE,
                                    height: AVATAR_SIZE,
                                    borderRadius: '15%',
                                    boxShadow: '0 2px 8px rgba(22, 24, 50, 0.25)',
                                    bgcolor: mainBlue,
                                    fontSize: 48,
                                    fontWeight: 'bold',
                                }}
                            >
                                {user.login?.charAt(0).toUpperCase()}
                            </Avatar>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                <InfoLine icon={<BadgeOutlinedIcon />} label='Login' value={user.login} />
                                <InfoLine icon={<EmailOutlinedIcon />} label='Email' value={user.email} />
                                <InfoLine icon={<BugReportOutlinedIcon />} label='Login Mantis' value={user.loginMantis} />
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 7 }}>
                <Card sx={CARD_SX}>
                    <CardTitle title='Rôles' color={darkBlue} />
                    <CardContent sx={CARD_CONTENT_SX}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 2, width: '100%' }}>
                            {HABILITATIONS.map(({ key, label, Icon }) => (
                                <RoleTile
                                    key={key}
                                    habKey={key}
                                    Icon={Icon}
                                    label={label}
                                    checked={!!user?.habs?.includes(key)}
                                    onToggle={() => changeHab(key)}
                                />
                            ))}
                        </Box>
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    )
}

export default UserApp
